import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useAuthToken } from './useAuthToken';
import { logger } from '../utils/logger';

// Constants for localStorage keys
const AUTO_LOGOUT_ENABLED = 'autoLogoutEnabled';

// Define the settings interface
type UserSettingsContextType = {
  autoLogoutEnabled: boolean;
  setAutoLogoutEnabled: (enabled: boolean) => void;
};

// Default settings
const initialType: UserSettingsContextType = {
  autoLogoutEnabled: true, // Auto logout is enabled by default
  setAutoLogoutEnabled: (enabled: boolean) => {},
};

// Create the context
const UserSettingsContext = createContext(initialType);

export const UserSettingsProvider = ({children}: { children: React.ReactNode }) => {
  logger.info('[UserSettings] UserSettingsProvider rendering');
  const { token } = useAuthToken();
  const [autoLogoutEnabled, setAutoLogoutEnabledState] = useState<boolean>(
    localStorage.getItem(AUTO_LOGOUT_ENABLED) !== 'false' // Default to true if not explicitly set to false
  );
  
  // Set auto logout enabled state
  const setAutoLogoutEnabled = useCallback((enabled: boolean) => {
    setAutoLogoutEnabledState(enabled);
    localStorage.setItem(AUTO_LOGOUT_ENABLED, enabled ? 'true' : 'false');
    document.dispatchEvent(new Event('storageChange'));
  }, []);
  
  // Listen for storage changes
  const onLocalStateChange = useCallback(event => {
    // eslint-disable-next-line eqeqeq
    if ((!event.storageArea || event.storageArea != localStorage) && event.type !== 'storageChange') return;
    
    setTimeout(() => {
      const newAutoLogoutEnabled = localStorage.getItem(AUTO_LOGOUT_ENABLED) !== 'false';
      setAutoLogoutEnabledState(prevState => {
        if (prevState !== newAutoLogoutEnabled) {
          return newAutoLogoutEnabled;
        }
        return prevState;
      });
    }, 50);
  }, []); // Remove autoLogoutEnabled dependency to prevent constant recreation
  
  // Set up event listeners
  useEffect(() => {
    document.addEventListener('storageChange', onLocalStateChange, false);
    window.addEventListener('storage', onLocalStateChange, false);
    
    return () => {
      document.removeEventListener('storageChange', onLocalStateChange);
      window.removeEventListener('storage', onLocalStateChange);
    };
  }, [onLocalStateChange]);
  
  // Reset settings when user changes
  useEffect(() => {
    if (!token) {
      // Reset to defaults when user logs out
      setAutoLogoutEnabledState(true);
    }
  }, [token]);
  
  return (
    <UserSettingsContext.Provider value={{
      autoLogoutEnabled,
      setAutoLogoutEnabled,
    }}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = () => useContext(UserSettingsContext);

// Function to initialize settings from user data
export const initUserSettingsFromUserData = (userData: any) => {
  if (userData && userData.autoLogoutEnabled !== undefined) {
    // Only initialize if the setting doesn't already exist in localStorage
    if (localStorage.getItem(AUTO_LOGOUT_ENABLED) === null) {
      localStorage.setItem(AUTO_LOGOUT_ENABLED, 
        userData.autoLogoutEnabled ? 'true' : 'false');
      logger.info(`[UserSettings] Auto logout setting initialized from user data: ${
        userData.autoLogoutEnabled ? 'enabled' : 'disabled'}`);
    } else {
      logger.info('[UserSettings] Auto logout setting already exists in localStorage, not overriding with user data');
    }
  }
};
